import React, {forwardRef} from 'react';
import {useTranslation} from 'react-i18next';

import {Card, IconLogOut, IconPerson, IconPersonGear} from '@veneer/core';
import Popover from '@veneer/core/dist/scripts/shared/popover';

import {useWolfTheme} from '../../../contexts';
import {A, ButtonLink} from '../../Links';

import useStyle from './styles';
import {UserAction} from './types';

/* eslint-disable react/display-name */
const UserCard = forwardRef(
    (
        {
            anchorEl,
            logoutLink,
            username,
            email,
            actions,
            manageAccountUrl,
        }: {
            anchorEl?: HTMLElement | null;
            logoutLink: string;
            username?: string;
            manageAccountUrl?: string;
            email?: string;
            actions?: UserAction[];
        },
        ref
    ) => {
        const {t} = useTranslation(['AppHeader']);

        const style = useStyle();
        const theme = useWolfTheme();

        const userProfileLabel = t('AppHeader:userProfile', 'User Profile');
        const logOutLabel = t('AppHeader:signOut', 'Sign Out');

        return (
            <Popover
                anchorEl={anchorEl}
                open
                style={{zIndex: theme.elevation.popover}}
                keepMounted
                // @ts-expect-error Problem with veneer ref types.
                ref={ref}
                placement="bottom"
            >
                <Card
                    appearance="dropShadow"
                    className={style.card}
                    data-testid="user-card"
                    content={
                        <>
                            <div className={style.cardInner}>
                                <div className={style.avatar}>
                                    <IconPerson size={36} />
                                </div>
                                <div>
                                    {username && (
                                        <p
                                            data-testid="UserMenu-username"
                                            className={style.username}
                                        >
                                            {username}
                                        </p>
                                    )}
                                    {email && <p className={style.email}>{email}</p>}

                                    {actions ? (
                                        <div className={style.actionsContainer}>
                                            <ul className={style.userMenuActions}>
                                                {actions.map(action => (
                                                    <li key={action.href}>
                                                        <A href={action.href}>{action.label}</A>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className={style.buttonContainer}>
                                    {manageAccountUrl && (
                                        <div className={style.buttonContainers}>
                                            {/* Here to maintain consistent spacing with the avatar section using a grid layout */}
                                            <div />
                                            <ButtonLink
                                                ariaLabel={userProfileLabel}
                                                small
                                                leadingIcon={<IconPersonGear aria-hidden />}
                                                href={manageAccountUrl}
                                                appearance="ghost"
                                            >
                                                {userProfileLabel}
                                            </ButtonLink>
                                        </div>
                                    )}
                                    <div className={style.buttonContainers}>
                                        {/* Here to maintain consistent spacing with the avatar section using a grid layout */}
                                        <div />
                                        <div>
                                            <ButtonLink
                                                small
                                                // eslint-disable-next-line react/forbid-component-props
                                                forceRedirect
                                                ariaLabel={logOutLabel}
                                                appearance="ghost"
                                                href={logoutLink}
                                                leadingIcon={<IconLogOut aria-hidden />}
                                            >
                                                {logOutLabel}
                                            </ButtonLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                />
            </Popover>
        );
    }
);

/* eslint-enable react/display-name */

export default UserCard;
